import { HUD } from '../../config/tuning';
import type { HudPainter } from '../HudLayout';
import { TextSlot } from '../HudLayout';
import type { HudFrame } from '../HudFrame';

/**
 * The camera gain readout (SPEC §3.3.2).
 *
 * Auto-exposure is one of the two post-processing effects that carry gameplay signal — a
 * sun-facing ingress genuinely hides threats — so a player who switches it off for accessibility
 * or performance must be told in words what the picture would otherwise have told them. It is
 * deliberately absent the rest of the time: with the effect running, the washed-out image *is*
 * the readout, and a second one would be clutter.
 *
 * Sits under the endurance readout at the bottom left, out of the way of the RWR.
 */

/** Amber once the camera is fighting the scene by more than this many stops. */
const WARN_STOPS = 1.5;

/** Gain band for the readout. Stops in, colour out; undefined means the normal HUD colour. */
export function exposureColour(stops: number): string | undefined {
  return Math.abs(stops) >= WARN_STOPS ? HUD.warnColour : undefined;
}

export function drawExposure(painter: HudPainter, frame: HudFrame): void {
  const stops = frame.exposureStops;
  if (stops === null) return;

  const layout = painter.layout;
  const margin = layout.px(HUD.marginPx);
  const gap = layout.px(HUD.readoutGapPx);

  const sign = stops >= 0 ? '+' : '−';
  const magnitude = painter.number(TextSlot.Exposure, Math.abs(stops), 1);
  painter.text(
    `GAIN ${sign}${magnitude}EV`,
    margin,
    layout.heightPx - margin - gap * 2,
    'left',
    exposureColour(stops),
  );
}
